import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogOut, User, BookOpen, BarChart3, Menu, X, Home, Filter, FileText, List } from 'lucide-react';

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setSidebarOpen(false);
  };

  const isActive = (path) => location.pathname === path;
  const isPublicPage = location.pathname === '/' || location.pathname === '/courses' || location.pathname.startsWith('/courses/');

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close sidebar when route changes
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [sidebarOpen]);

  const navLinks = [
    { to: '/dashboard', label: 'Dashboard', icon: Home },
    { to: '/master', label: 'Master Type', icon: BookOpen },
    { to: '/types', label: 'Sub Type', icon: List },
    { to: '/syllabus', label: 'Syllabus', icon: FileText },
    { to: '/filtered', label: 'Filtered', icon: Filter },
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 ${scrolled ? 'bg-white shadow-sm border-b border-gray-200' : 'bg-transparent'
        }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            {/* Left: Logo and Hamburger Menu */}
            <div className="flex items-center space-x-4">
              {!isPublicPage && (
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="xl:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label="Toggle menu"
                >
                  {sidebarOpen ? (
                    <X className="w-6 h-6 text-gray-700" />
                  ) : (
                    <Menu className="w-6 h-6 text-gray-700" />
                  )}
                </button>
              )}

              <div className="flex items-center relative">
                <Link to={isAuthenticated ? "/dashboard" : "/"} className="text-2xl font-bold text-gradient">
                  Exam Prasna
                </Link>
                {!isAuthenticated && !isPublicPage && (
                  <span className="absolute -top-2 -right-16 bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full border border-blue-200">
                    Demo Mode
                  </span>
                )}
              </div>
            </div>

            {/* Center: Navigation Links - Desktop only (hidden below 1150px) */}
            {!isPublicPage && (
              <div className="hidden xl:flex items-center space-x-1 bg-white/50 backdrop-blur-sm px-2 py-1 rounded-full border border-gray-200/50">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${isActive(link.to)
                      ? 'bg-blue-600 text-white shadow-md transform scale-105'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                      }`}
                  >
                    <link.icon className={`w-4 h-4 ${isActive(link.to) ? 'text-white' : ''}`} />
                    <span>{link.label}</span>
                  </Link>
                ))}
              </div>
            )}

            {/* Right: User Profile */}
            <div className="flex items-center">
              {!isPublicPage ? (
                isAuthenticated ? (
                  <div className="relative">
                    <button
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className="flex items-center space-x-3 p-1.5 pr-3 rounded-full hover:bg-gray-100 transition-colors border border-transparent hover:border-gray-200"
                    >
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-sm">
                        <span className="text-white font-semibold text-sm">
                          {user?.name?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                      <span className="hidden md:block font-medium text-sm text-gray-700">{user?.name}</span>
                    </button>

                    {/* Dropdown */}
                    {dropdownOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setDropdownOpen(false)}
                        ></div>
                        <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 z-20 overflow-hidden animate-fade-in">
                          <div className="p-4 border-b border-gray-50 bg-gray-50/50">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold shadow-sm">
                                {user?.name?.charAt(0).toUpperCase() || 'U'}
                              </div>
                              <div>
                                <div className="font-semibold text-gray-900">{user?.name}</div>
                                <div className="text-xs text-gray-500 truncate max-w-[140px]">{user?.email}</div>
                              </div>
                            </div>
                          </div>
                          <div className="p-2">
                            <Link
                              to="/dashboard"
                              className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-blue-50 text-gray-700 hover:text-blue-600 transition-colors"
                              onClick={() => setDropdownOpen(false)}
                            >
                              <User className="w-4 h-4" />
                              <span className="text-sm font-medium">My Profile</span>
                            </Link>
                            <hr className="my-2 border-gray-100" />
                            <button
                              onClick={handleLogout}
                              className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-red-50 text-gray-700 hover:text-red-600 transition-colors w-full text-left"
                            >
                              <LogOut className="w-4 h-4" />
                              <span className="text-sm font-medium">Logout</span>
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center space-x-3">
                    <Link to="/login" className="hidden sm:block text-gray-600 hover:text-blue-600 font-medium text-sm">
                      Login
                    </Link>
                    <Link
                      to="/login"
                      className="btn-primary px-4 py-2 rounded-full text-sm font-medium shadow-lg shadow-blue-500/20"
                    >
                      Get Started
                    </Link>
                  </div>
                )
              ) : (
                <div className="flex items-center space-x-3">
                  <Link to="/login" className="hidden sm:block text-gray-600 hover:text-blue-600 font-medium text-sm">
                    Login
                  </Link>
                  <Link to="/login" className="btn-primary px-5 py-2 rounded-full text-sm font-medium shadow-lg shadow-blue-500/20">
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Sidebar */}
      {!isPublicPage && (
        <>
          <div
            className={`fixed inset-0 bg-black/20 backdrop-blur-sm z-40 transition-opacity duration-300 xl:hidden ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            onClick={() => setSidebarOpen(false)}
          />

          <div
            className={`fixed top-0 left-0 h-full w-80 bg-white/95 backdrop-blur-xl shadow-2xl z-50 transform transition-transform duration-300 ease-out xl:hidden border-r border-gray-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
              }`}
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-6 border-b border-gray-100">
                <span className="text-2xl font-bold text-gradient">Exam Prasna</span>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {isAuthenticated && (
                <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50/50 border-b border-blue-100/50">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm border border-blue-100 text-blue-600 font-bold text-lg">
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">{user?.name}</div>
                      <div className="text-xs text-gray-500">{user?.email}</div>
                    </div>
                  </div>
                </div>
              )}

              <nav className="flex-1 overflow-y-auto p-4 space-y-2">
                {navLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 border ${isActive(link.to)
                        ? 'bg-blue-500 text-white font-semibold shadow-md border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <Icon className={`w-5 h-5 ${isActive(link.to) ? 'text-white' : 'text-gray-400'}`} />
                      <span>{link.label}</span>
                    </Link>
                  );
                })}
              </nav>

              <div className="p-4 border-t border-gray-100">
                {isAuthenticated ? (
                  <button
                    onClick={handleLogout}
                    className="flex items-center justify-center space-x-2 w-full px-4 py-3 rounded-xl bg-red-50 text-red-600 font-medium hover:bg-red-100 transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Logout</span>
                  </button>
                ) : (
                  <div className="space-y-3">
                    <Link
                      to="/login"
                      className="block w-full text-center px-4 py-3 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                    >
                      Login
                    </Link>
                    <Link
                      to="/login"
                      className="block w-full text-center btn-primary text-white px-4 py-3 rounded-xl font-medium shadow-lg shadow-blue-500/20"
                    >
                      Get Started
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Navbar;
